import { Building2 } from 'lucide-react'
import { Legend, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts'
import type { Account } from '@/types/serializers'
import { Card, CardContent, CardHeader } from "@/components/ui/card.tsx";

interface CompetitiveAnalysisTabProps {
    account: Account
}

export default function CompetitiveAnalysisTab({account}: CompetitiveAnalysisTabProps) {
    const chartData = [
        {metric: "Claims Handling", score: 58},
        {metric: "Sales & Onboarding", score: 65},
        {metric: "Issue Resolution", score: 56},
        {metric: "Renewal & Cancellation", score: 55},
        {metric: "Outbound Communication", score: 58},
        {metric: "Brand Trust", score: 75},
        {metric: "Product Offering", score: 80},
        {metric: "MetaSurvey Index", score: 60},
        {metric: "Value for Money", score: 60},
    ];


    const competitiveAnalysis = account.competitive_analysis
    // Early return if no competitive analysis data
    if (!competitiveAnalysis || !competitiveAnalysis.categories?.length) {
        return (
            <div
                className="bg-white dark:bg-dark-bg-400 border border-neutral-200 dark:border-dark-border-light rounded-lg p-8">
                <div className="text-center py-12">
                    <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4"/>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-neutral-50 mb-2">
                        Competitive Analysis Not Available
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                        Competitive analysis data for this account is not available.
                    </p>
                </div>
            </div>
        )
    }

    // Transform data for the radar chart - add numbered categories for display
    const radarData = competitiveAnalysis.categories.map((category, index) => {
        const numberedCategory = `${index + 1} ${category}`
        const dataPoint: any = {category: numberedCategory}

        // Add account scores
        if (competitiveAnalysis.scores.account[index] !== undefined) {
            dataPoint[account.name] = competitiveAnalysis.scores.account[index]
        }

        // Add competitor scores
        Object.entries(competitiveAnalysis.scores.competitors).forEach(([company, scores]) => {
            if (scores[index] !== undefined) {
                dataPoint[company] = scores[index]
            }
        })

        return dataPoint
    })

    // Use performance insights from the value object
    const insights = competitiveAnalysis.insights || {}
    const strongest = insights.strongest || {}
    const weakest = insights.weakest || {}
    const overall = insights.overall || {}

    // Define colors based on company index
    const colorPalette = [
        '#ef4444', // Primary red for client
        '#84cc16', // Lime
        '#10b981', // Green
        '#6366f1', // Blue
        '#8b5cf6', // Purple
        '#f59e0b', // Amber
        '#06b6d4', // Cyan
        '#f97316', // Orange
        '#ec4899', // Pink
        '#64748b'  // Slate
    ]

    // Create color mapping for all companies (account + competitors)
    const allCompanies = [account.name, ...Object.keys(competitiveAnalysis.scores.competitors)]
    const companyColors: any = {}
    allCompanies.forEach((company, index) => {
        companyColors[company] = colorPalette[index % colorPalette.length]
    })

    return (
        <Card>
            <CardContent>
                <div className="space-y-6">
                    {/* Radar Chart Section */}
                    <div className="flex items-center space-x-2 mb-6">
                        <Building2 className="w-5 h-5 text-slate-600 dark:text-slate-400"/>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                            {account.name} Competitive Analysis
                        </h3>
                    </div>

                    {/* Radar Chart */}
                    <div className="w-full h-[400px] flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={400}>
                            <RadarChart data={radarData} margin={{top: 20, right: 80, bottom: 20, left: 80}}>
                                <PolarGrid gridType="polygon"/>
                                <PolarAngleAxis
                                    dataKey="category"
                                    tick={{
                                        fontSize: 11,
                                        fill: 'currentColor',
                                        className: 'text-slate-700 dark:text-neutral-300'
                                    }}
                                    className="text-slate-700 dark:text-neutral-300"
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[60, 95]}
                                    tick={{
                                        fontSize: 9,
                                        fill: 'currentColor',
                                        className: 'text-slate-500 dark:text-slate-400'
                                    }}
                                    tickCount={5}
                                />

                                {/* Account data - primary line */}
                                <Radar
                                    name={account.name}
                                    dataKey={account.name}
                                    stroke={companyColors[account.name]}
                                    fill={companyColors[account.name]}
                                    fillOpacity={0.15}
                                    strokeWidth={3}
                                />

                                {/* Competitor data */}
                                {Object.keys(competitiveAnalysis.scores.competitors).map(company => (
                                    <Radar
                                        key={company}
                                        name={company}
                                        dataKey={company}
                                        stroke={companyColors[company] || '#9ca3af'}
                                        fill="transparent"
                                        strokeWidth={3}
                                    />
                                ))}

                                <Legend
                                    wrapperStyle={{
                                        paddingTop: '20px'
                                    }}
                                    iconType="line"
                                />
                            </RadarChart>
                        </ResponsiveContainer>

                        {/*<RadarChart2 chartData={chartData}/>*/}
                    </div>

                    {/* Performance Summary */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">

                        <Card>
                            <CardHeader>
                                <h4 className="font-medium text-slate-900 dark:text-neutral-50 mb-2">Strongest
                                    Performance</h4>
                            </CardHeader>
                            <CardContent>

                                <p className="text-sm text-slate-600 dark:text-neutral-300">{strongest.category || 'N/A'}</p>
                                <p className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                                    {strongest.score ? `${strongest.score}/100` : 'N/A'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {strongest.assessment || ''}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <h4 className="font-medium text-slate-900 dark:text-neutral-50">Improvement
                                    Area</h4>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-slate-600 dark:text-neutral-300">{weakest.category || 'N/A'}</p>
                                <p className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                                    {weakest.score ? `${weakest.score}/100` : 'N/A'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {weakest.assessment || ''}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <h4 className="font-medium text-slate-900 dark:text-neutral-50">Overall Score</h4>
                            </CardHeader>
                            <CardContent>
                                <p className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                                    {overall.score ? `${overall.score}/100` : 'N/A'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {overall.assessment || ''}
                                </p>
                            </CardContent>

                        </Card>
                    </div>

                    {/* Pain Point Analysis Section */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                                {account.name} Pain Point Analysis
                            </h3>
                        </CardHeader>

                        <CardContent>
                            <div className="prose prose-sm max-w-none text-slate-600 dark:text-neutral-300">
                                {competitiveAnalysis.analysis?.pain_points ? (
                                    competitiveAnalysis.analysis.pain_points.split('\n\n').map((paragraph, index) => (
                                        <p key={index} className="mb-4 leading-relaxed">
                                            {paragraph}
                                        </p>
                                    ))
                                ) : (
                                    <p className="text-slate-500 dark:text-slate-400">
                                        Pain point analysis not available for this account.
                                    </p>
                                )}
                            </div>

                        </CardContent>
                    </Card>


                    {/* Strategic Recommendations Section */}
                    {competitiveAnalysis.analysis?.recommendations?.length > 0 && (
                        <Card>
                            <CardHeader>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50">
                                    Strategic Recommendations
                                </h3>
                            </CardHeader>
                            <CardContent>
                                {competitiveAnalysis.analysis.recommendations.map((recommendation, index) => (
                                    <div key={index} className="flex items-start space-x-3">
                                        <div
                                            className="flex-shrink-0 w-6 h-6 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300">
                                            {index + 1}
                                        </div>
                                        <p className="text-slate-600 dark:text-neutral-300 leading-relaxed">
                                            {recommendation}
                                        </p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}